import { z } from 'zod';
import { asArray, asRecord } from '@/lib/json';

type T = (key: string, opts?: Record<string, unknown>) => string;

/** One inline rule (02 §7.10). `severity: flag` lands in `flags`, anything else in `errors`. */
export interface RuleRow {
  id: string;
  when: string;
  assert: string;
  severity: 'error' | 'flag';
  message: string;
  path: string;
}

export function rulesFromJson(rules: unknown): RuleRow[] {
  return asArray(rules).map((r) => {
    const o = asRecord(r);
    const s = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : '');
    return { id: s('id'), when: s('when'), assert: s('assert'), severity: o['severity'] === 'flag' ? 'flag' : 'error', message: s('message'), path: s('path') };
  });
}

/** Serialises rules, omitting empty optional fields so stored JSON stays minimal. */
export function rulesToJson(rows: readonly RuleRow[]): Record<string, string>[] {
  return rows.map((r) => {
    const o: Record<string, string> = { id: r.id.trim(), assert: r.assert.trim(), message: r.message.trim(), severity: r.severity };
    if (r.when.trim()) o['when'] = r.when.trim();
    if (r.path.trim()) o['path'] = r.path.trim();
    return o;
  });
}

export function rulesetSchema(t: T) {
  const rule = z.object({
    id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_.-]{0,63}$/, t('admin.validation.ruleId')),
    when: z.string(),
    assert: z.string().trim().min(1, t('admin.validation.required')),
    severity: z.enum(['error', 'flag']),
    message: z.string().trim().min(1, t('admin.validation.required')),
    path: z.string(),
  });
  return z.object({
    key: z.string().regex(/^[a-z0-9][a-z0-9.-]{1,63}$/, t('admin.validation.rulesetKey')),
    name: z.string().trim().min(1, t('admin.validation.required')),
    description: z.string(),
    rules: z
      .array(rule)
      .min(1, t('admin.validation.ruleRequired'))
      .superRefine((rows, ctx) => {
        const seen = new Set<string>();
        rows.forEach((r, i) => {
          if (seen.has(r.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'id'], message: t('admin.validation.duplicateRuleId') });
          seen.add(r.id);
        });
      }),
  });
}
export type RulesetForm = z.infer<ReturnType<typeof rulesetSchema>>;

export interface RulesOutcome {
  ok: boolean;
  errors: { id: string; message: string; path?: string | null }[];
  flags: { id: string; message: string; path?: string | null }[];
}

export function parseOutcome(v: unknown): RulesOutcome {
  const o = asRecord(v);
  const list = (k: string) =>
    asArray(o[k]).map((x) => {
      const e = asRecord(x);
      return { id: String(e['id'] ?? ''), message: String(e['message'] ?? ''), path: typeof e['path'] === 'string' ? e['path'] : null };
    });
  return { ok: o['ok'] === true, errors: list('errors'), flags: list('flags') };
}
